import CartIcon from '@components/icons/cart-icon'
import LabelIcon from '@components/icons/label-icon'
import RelatedProductFeed from '@components/product/feeds/related-product-feed'
import ProductAttributes from '@components/product/product-attributes'
import ProductDetailsTab from '@components/product/product-details/product-tab'
import Button from '@components/ui/button'
import ThumbnailCarousel from '@components/ui/carousel/thumbnail-carousel'
import Counter from '@components/ui/counter'
import Image from '@components/ui/image'
import TagLabel from '@components/ui/tag-label'
import { useCart } from '@contexts/cart/cart.context'
import usePrice from '@framework/product/use-price'
import { getRelatedProducts } from '@framework/typesense'
import { convertProduct } from '@framework/utils/convert-product'
import { getVariations } from '@framework/utils/get-variations'
import { generateCartItem } from '@utils/generate-cart-item'
import { ROUTES } from '@utils/routes'
import useWindowSize from '@utils/use-window-size'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import StarRatingComponent from 'react-star-rating-component'
import { toast } from 'react-toastify'
import StarIcon from '@components/icons/star-icon'
import ProductAside from './product-aside'
import ProductCase from './product-case'
import ProductTitle from './product-title'
import ProductSubstitute from './product-substitute'
import { IoStarSharp } from 'react-icons/io5'
import ProductfromManufacturer from './from-manufacturer.tsx'
import ProductReviewsAndQuestion from './product-reviews-questions'
import ProductCompare from './product-compare'
import ProductComparewithOthers from './compare-products'
import ProductStickyBar from './product-stickybar'
import { ChangeEvent, FC, useContext, useEffect, useState } from 'react'
import VariationPrice from './variation-price'

import { useModalAction } from '@components/common/modal/modal.context'
import { productGalleryPlaceholder } from '@assets/placeholders'
import ProductClickCollect from './product-clickcollect'
import { useCheckout } from '@contexts/checkout/checkout.context'
import FrequentlyBoughtTogetherFeed from './feeds/frequently-bought-together-feed'
import Breadcrumb from '@components/ui/breadcrumb'
import ProductTags from './product-tags'
import ProductAsideMobile from './product-aside-mobile'
import Link from 'next/link'
import pdpReviewsService from '@services/pdp-reviews-service'
import pdpService from '@services/pdp-service'
import cartServices from '@services/cartservices'
import { DEFAULT_CODE, REVIEWS_SECTION } from '@constants/index'
import { UserContext } from '@contexts/user/user.context'
import { IUser } from '@contexts/user/user.utils'

const breakpoints = {
  '1536': {
    slidesPerView: 6,
  },
  '1280': {
    slidesPerView: 5,
  },
  '1024': {
    slidesPerView: 4,
  },
  '640': {
    slidesPerView: 3,
  },
  '360': {
    slidesPerView: 2,
  },
  '0': {
    slidesPerView: 1,
  },
}

interface ProductSingleDetailsProps {
  initialProduct
  getUpdatedProduct: (id: number) => Promise<void>
  product: any
  pathname: string
}

const ProductSingleDetails: FC<ProductSingleDetailsProps> = ({
  initialProduct,
  product,
  pathname,
  getUpdatedProduct,
}: any) => {
  const { t } = useTranslation('common')
  const router = useRouter()
  const {
    locale,
    query: { slug },
  } = router
  const { width } = useWindowSize()
  const { shippingInformation } = useContext(UserContext) as IUser
  const [isShowSpecialOrderDesc, setIsShowSpecialOrderDesc] = useState(false)
  const data = convertProduct(product, locale)
  const brandData = data?.mag_attributes?.find(
    (attr: { attribute_code: string }) => attr.attribute_code === 'mag_brand',
  )?.value[0]
  const productReview = data?.attributes?.product_review
    ? JSON.parse(data.attributes.product_review)
    : null
  const {
    addItemToCart,
    isInCart,
    getItemFromCart,
    isInStock,
    callShippingInformation,
  } = useCart()
  const { magConfig } = useCheckout()
  const [selectedQuantity, setSelectedQuantity] = useState(1)
  const [attributes, setAttributes] = useState<{ [key: string]: string }>({})
  const [favorite, setFavorite] = useState<boolean>(false)
  const [quantity, setQuantity] = useState(data?.quantity || 2000)
  const [relatedItems, setRelatedItems] = useState([])
  const [addToCartLoader, setAddToCartLoader] = useState<boolean>(false)
  const [rating_custom_icon, set_rating_custom_icon] = useState(5)
  const [kingstonClicked, setKingstonClicked] = useState(false)
  const [addToWishlistLoader, setAddToWishlistLoader] = useState<boolean>(false)
  const [shareButtonStatus, setShareButtonStatus] = useState<boolean>(false)
  const [selectedOptions, setSelectedOptions] = useState({})
  const [selectedSku, setSelectedSku] = useState(null)
  const [expectedDelivery, setExpectedDelivery] = useState(null)
  const [visibleSections, setVisibleSections] = useState<{
    [key: string]: boolean
  }>({})
  const productUrl = `${process.env.NEXT_PUBLIC_WEBSITE_URL}${ROUTES.PRODUCT}/${router.query.slug}`
  const { price, basePrice, discount } = usePrice(
    data && {
      amount: data.sale_price ? data.sale_price : data.price,
      baseAmount: data.price,
      currencyCode: process.env.NEXT_PUBLIC_CURRENCY_CODE || 'USD',
    },
  )

  const [reviews, setReviews] = useState<any>([])

  const toggleSectionVisibility = (sectionId: string) => {
    setVisibleSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }))
  }

  const getExpectedDelivery = async () => {
    const payload: any = {
      destinationZipcode: shippingInformation?.postcode
        ? shippingInformation?.postcode
        : DEFAULT_CODE,
      sku: product?.sku,
    }
    try {
      let response: any = await pdpService.productDeliveryDates(payload)
      setExpectedDelivery(response.delivery_by)
      callShippingInformation()
    } catch (err) {
      console.error(err?.message)
    }
  }

  useEffect(() => {
    if (shippingInformation?.postcode || DEFAULT_CODE) {
      getExpectedDelivery()
    }
  }, [shippingInformation?.postcode])

  useEffect(() => {
    const getReviewsData = async () => {
      try {
        let response: any = await pdpReviewsService.getProductReviews(
          product.sku,
        )
        const modifyData = pdpReviewsService.processReviewsData(response)
        setReviews(modifyData)
      } catch (err) {
        console.error(err?.message)
      }
    }
    getReviewsData()
  }, [])

  useEffect(() => setSelectedQuantity(1), [product])
  useEffect(() => {
    getRelatedProducts(product, locale || 'en-CA').then((hits: any) =>
      setRelatedItems(hits),
    )
  }, [router, product, product.sku, locale])

  const handleChange = () => {
    setShareButtonStatus(!shareButtonStatus)
  }
  const variations = getVariations(data?.variations)

  const isSelected = !isEmpty(variations)
    ? !isEmpty(attributes) &&
      Object.keys(variations).every((variation) =>
        attributes.hasOwnProperty(variation),
      )
    : true
  let selectedVariation: any = {}
  if (isSelected) {
    const dataVaiOption: any = data?.variation_options
    selectedVariation = dataVaiOption?.find((o: any) =>
      isEqual(
        o.options.map((v: any) => v.value).sort(),
        Object.values(attributes).sort(),
      ),
    )
  }
  const item = generateCartItem(data!, selectedVariation)
  const outOfStock = isInCart(item.id) && !isInStock(item.id)
  function addToCart() {
    if (!isSelected) return
    // to show btn feedback while product carting
    setAddToCartLoader(true)
    setTimeout(() => {
      setAddToCartLoader(false)
      setIsShowSpecialOrderDesc(false)
    }, 1500)

    const item: any = generateCartItem(data!, selectedVariation)
    item.isClickCollect = kingstonClicked

    addItemToCart(item, selectedQuantity)
  }

  function addToWishlist() {
    // to show btn feedback while product wishlist
    setAddToWishlistLoader(true)
    setFavorite(!favorite)
    const toastStatus: string =
      favorite === true ? t('text-remove-favorite') : t('text-added-favorite')
    setTimeout(() => {
      setAddToWishlistLoader(false)
    }, 1500)
    toast(toastStatus, {
      progressClassName: 'fancy-progress-bar',
      position: width! > 768 ? 'bottom-right' : 'top-right',
      autoClose: 1500,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    })
  }

  function RenderPopupOrAddToCart({ props }: { props: Object }) {
    let { data }: any = props
    const { t } = useTranslation('common')
    const { id, quantity, product_type } = data ?? {}
    const { width } = useWindowSize()
    const { openModal } = useModalAction()
    const { isInCart, isInStock } = useCart()
    const iconSize = width! > 1024 ? '19' : '17'
    const outOfStock = isInCart(id) && !isInStock(id)
    const handlePopupView = () => {
      openModal('CUSTOM_QUOTE', data)
    }

    return (
      <>
        <div className="pt-6 pb-2 md:pt-7">
          <span>{t('text-looking-bulk')}</span>
        </div>
        <div className="flex items-center">
          <Image
            src="/assets/images/tag.svg"
            alt="custom-quote-icon"
            width={40}
            height={40}
          />
          <a
            onClick={handlePopupView}
            className="text-md cursor-pointer font-semibold lg:text-15px text-brand-dark focus:outline-none ltr:ml-2 hover:underline text-brand-green"
          >
            {t('text-get-quote')}
          </a>
        </div>
      </>
    )
  }

  const inputChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    let numberFlag = isNaN(parseInt(event.target.value))
    let newValue = numberFlag ? 1 : parseInt(event.target.value)
    if (newValue < 1) return false
    setSelectedQuantity(newValue)
  }
  const moveToReviews = () => {
    window.location.href = `#${REVIEWS_SECTION}`
  }
  const handleQuantityChange = (quantity: number) => {
    setSelectedQuantity(quantity)
  }
  return (
    <div className="max-w-[1920px] mx-auto">
      <div className="pt-6 pb-2 md:pt-7  px-4 md:px-6 lg:px-8 2xl:px-10">
        <Breadcrumb pathname={pathname} />
        <ProductTitle product={product} />

        <div className="flex items-center gap-4 my-3">
          <StarRatingComponent
            name="app3"
            starCount={5}
            value={JSON.parse(product?.attributes?.product_review)?.rating_avg}
            starColor="#FFA502"
            editing={false}
            emptyStarColor="#DFE6ED"
            renderStarIcon={() => <IoStarSharp className="-mr-1" />}
          />

          {reviews?.reviewData?.length ? (
            <div className="text-sm">
              <span
                onClick={moveToReviews}
                className="text-brand font-medium underline hover:cursor-pointer hover:no-underline"
              >
                {reviews.reviewData.length} Review
                {reviews.reviewData.length > 1 && 's'}
              </span>
            </div>
          ) : (
            <div className="text-sm">
              <span
                onClick={moveToReviews}
                className="text-brand font-medium underline hover:cursor-pointer hover:no-underline"
              >
                {t('text-write-review')}
              </span>
            </div>
          )}

          <div className="text-brand-dark font-medium text-sm md:text-xl xl:text-[14px]">
            SKU ID: #{product?.sku}
          </div>
          {/* <ProductCase product={product} /> */}
        </div>
        <div className="block lg:hidden">
          {data?.is_in_stock && (
            <ProductTags selectedSku={selectedSku} data={data} />
          )}
        </div>
        <div className="grid-cols-12 lg:grid gap-7 2xl:gap-8">
          <div className="col-span-5 lg:col-span-7 mb-6 overflow-hidden xl:col-span-8 md:mb-8 lg:mb-0">
            <ThumbnailCarousel
              gallery={data?.gallery}
              is_in_stock={data?.is_in_stock}
              thumbnailClassName="pb-16 sm:pb-6 pt-6"
              galleryClassName="xl:w-[120px] 2xl:w-[150px]"
              product={product}
              aspectRatio="20 / 10"
              isPdp={true}
              isPlaceholder={!data?.gallery?.length}
            />

            {data?.substitute_product && !data?.is_in_stock && (
              <ProductSubstitute />
            )}
            <div className="mt-6 block lg:hidden">
              <ProductAsideMobile
                getUpdatedProduct={getUpdatedProduct}
                toggleSectionVisibility={toggleSectionVisibility}
                selectedOptions={selectedOptions}
                setSelectedOptions={setSelectedOptions}
                setSelectedSku={setSelectedSku}
                setVisibleSections={setVisibleSections}
                selectedSku={selectedSku}
                visibleSections={visibleSections}
                product={product}
                data={data}
                item={item}
                isSelected={isSelected}
                selectedVariation={selectedVariation}
                expectedDelivery={expectedDelivery}
                initialProduct={initialProduct}
                onQuantityChange={handleQuantityChange}
                selectedQuantity={selectedQuantity}
                isShowSpecialOrderDesc={isShowSpecialOrderDesc}
                setIsShowSpecialOrderDesc={setIsShowSpecialOrderDesc}
              />
            </div>
            <ProductDetailsTab product={product} />
            <div className="mt-6 block lg:hidden">
              {brandData?.brand_url_key && (
                <div className="border p-4 py-7 mt-5 flex flex-col items-center">
                  {brandData?.brand_image && (
                    <Image
                      src={brandData?.brand_image}
                      alt={'dffg'}
                      // className="rounded-md w-36 lg:w-44"
                      width={427}
                      height={215}
                      // style={{ width: 'auto' }}
                      priority
                    />
                  )}
                  {/* <Link
                    href="#"
                    className="mt-4 text-brand text-lg font-medium underline cursor-pointer"
                  >
                    View All {brandData?.brand_titile} Products
                  </Link> */}
                </div>
              )}
              {/* <Image
                src="/assets/images/sponsored-image.svg"
                alt={'versatile-icon'}
                className="mt-5"
                width={610}
                height={344}
                // style={{ width: 'auto' }}
                priority
              /> */}
            </div>
            {/* {data?.cross_sell_products && ( */}
            {/* <RelatedProductFeed
              carouselBreakpoint={breakpoints}
              className="mb-0.5 md:mb-2 lg:mb-3.5 xl:mb-4 2xl:mb-6"
              hits={relatedItems}
            /> */}
            {/* )} */}
            {/* {data?.from_the_manufacturer && <ProductfromManufacturer />} */}
          </div>
          <div className="flex flex-col lg:col-span-5 col-span-5 shrink-0 xl:col-span-4  xl:rtl:pr-2 ">
            <div className="hidden lg:block">
              <ProductAside
                getUpdatedProduct={getUpdatedProduct}
                toggleSectionVisibility={toggleSectionVisibility}
                selectedOptions={selectedOptions}
                setSelectedOptions={setSelectedOptions}
                setSelectedSku={setSelectedSku}
                setVisibleSections={setVisibleSections}
                selectedSku={selectedSku}
                visibleSections={visibleSections}
                product={product}
                data={data}
                item={item}
                isSelected={isSelected}
                selectedVariation={selectedVariation}
                expectedDelivery={expectedDelivery}
                initialProduct={initialProduct}
                onQuantityChange={handleQuantityChange}
                selectedQuantity={selectedQuantity}
                isShowSpecialOrderDesc={isShowSpecialOrderDesc}
                setIsShowSpecialOrderDesc={setIsShowSpecialOrderDesc}
              />
            </div>
            {/* <div className="pb-3 lg:pb-5">
            <div className="md:mb-2.5 block -mt-1.5">
              <h2 className="text-lg font-medium transition-colors duration-300 text-brand-dark md:text-xl xl:text-2xl capitalize">
                {product.brand &&
                  product.name &&
                  product.name.indexOf(String(product.brand)) === -1
                  ? String(product.brand).toLocaleLowerCase()
                  : ''}{' '}
                {String(product.name).toLocaleLowerCase()}{' '}
                {product?.attributes?.case_description
                  ? `(${product.attributes.case_description.toLocaleLowerCase()})`
                  : ''}{' '}
                {product?.case_quantity ? (
                  <p>{`${product.case_quantity} ${t('text-per-case')}`}</p>
                ) : ''}
              </h2>
            </div>
            <div className="flex mt-2">
              {data?.flags.includes(LOCAL_INV_FLAG) && (
                <span className="text-md lg:text-18px font-semibold text-brand-green">
                  {t('text-local-kingston-inventory')}
                </span>
              )}
            </div>
            <div className="flex items-center mt-2">
              <div className="text-brand-dark font-medium text-base md:text-xl xl:text-[16px]">
                SKU ID: #{product?.sku}
              </div>
            </div>
            {product?.attributes?.case_description && isEmpty(variations) ? (
              <div className="text-sm font-medium md:text-15px capitalize">
                {product?.attributes?.case_type.toLocaleUpperCase() === 'CAISSE' || product?.attributes?.case_type.toLocaleUpperCase() === 'CASE'
                  ? t('text-case-type')
                  : product?.attributes?.case_type}
                : {(product?.attributes?.case_description || '').toLocaleLowerCase()}
              </div>
            ) : (
              <VariationPrice
                selectedVariation={selectedVariation}
                minPrice={data?.min_price}
                maxPrice={data?.max_price}
              />
            )}

            {isEmpty(variations) && (
              <div className="flex items-center mt-5">
                <div className="text-brand-dark font-bold text-base md:text-xl xl:text-[22px]">
                  {price}
                </div>
                {discount && (
                  <>
                    <del className="text-sm text-opacity-50 md:text-15px ltr:pl-3 rtl:pr-3 text-brand-dark">
                      {basePrice}
                    </del>
                    <span className="inline-block rounded font-bold text-xs md:text-sm bg-brand-tree bg-opacity-20 text-brand-tree uppercase px-2 py-1 ltr:ml-2.5 rtl:mr-2.5">
                      {discount} {t('text-off')}
                    </span>
                  </>
                )}
              </div>
            )}
          </div> */}

            {/* {Object.keys(variations).map((variation) => {
            return (
              <ProductAttributes
                key={`popup-attribute-key${variation}`}
                variations={variations}
                attributes={attributes}
                setAttributes={setAttributes}
              />
            );
          })} */}

            {/* <div className="pb-2">
            {true && <RenderPopupOrAddToCart props={{ data: product }} />}

            
            {isEmpty(variations) && (
              <>
                {Number(quantity) > 0 || !outOfStock ? (
                  <span className="text-sm font-medium text-yellow hidden">
                    {t('text-only') +
                      ' ' +
                      quantity +
                      ' ' +
                      t('text-left-item')}
                  </span>
                ) : (
                  <div className="text-base text-red-500 whitespace-nowrap">
                    {t('text-out-stock')}
                  </div>
                )}
              </>
            )}

            {!isEmpty(selectedVariation) && (
              <span className="text-sm font-medium text-yellow hidden">
                {selectedVariation?.is_disable ||
                selectedVariation.quantity === 0
                  ? t('text-out-stock')
                  : `${
                      t('text-only') +
                      ' ' +
                      selectedVariation.quantity +
                      ' ' +
                      t('text-left-item')
                    }`}
              </span>
            )}
          </div> */}

            <div className="pt-1.5 lg:pt-3 xl:pt-4 space-y-2.5 md:space-y-3.5">
              {/* <Counter
              variant="single"
              value={selectedQuantity}
              onIncrement={() => setSelectedQuantity((prev) => prev + 1)}
              onDecrement={() =>
                setSelectedQuantity((prev) => (prev !== 1 ? prev - 1 : 1))
              }
              onInputChange={inputChangeHandler}
              dataTestId="pdp"
              disabled={
                isInCart(item.id)
                  ? getItemFromCart(item.id).quantity + selectedQuantity >=
                    Number(item.stock)
                  : selectedQuantity >= Number(item.stock)
              }
            /> */}
              {/* <afterpay-placement
              data-locale="en_CA"
              data-badge-theme="black-on-mint"
              data-amount={price}
            ></afterpay-placement>
            <Button
              onClick={addToCart}
              className="w-full px-1.5"
              disabled={!isSelected}
              loading={addToCartLoader}
            >
              <CartIcon color="#ffffff" className="ltr:mr-3 rtl:ml-3" />
              {t('text-add-to-cart')}
            </Button> */}
              {/* <div className="grid gri;
            d-cols-2 gap-2.5">
              <Button
                variant="border"
                onClick={addToWishlist}
                loading={addToWishlistLoader}
                className={`group hover:text-brand ${
                  favorite === true && 'text-brand'
                }`}
              >
                {favorite === true ? (
                  <IoIosHeart className="text-2xl md:text-[26px] ltr:mr-2 rtl:ml-2 transition-all" />
                ) : (
                  <IoIosHeartEmpty className="text-2xl md:text-[26px] ltr:mr-2 rtl:ml-2 transition-all group-hover:text-brand" />
                )}

                {t('text-wishlist')}
              </Button>
              <div className="relative group">
                <Button
                  variant="border"
                  className={`w-full hover:text-brand ${
                    shareButtonStatus === true && 'text-brand'
                  }`}
                  onClick={handleChange}
                >
                  <IoArrowRedoOutline className="text-2xl md:text-[26px] ltr:mr-2 rtl:ml-2 transition-all group-hover:text-brand" />
                  {t('text-share')}
                </Button>
                <SocialShareBox
                  className={`absolute z-10 ltr:right-0 rtl:left-0 w-[300px] md:min-w-[400px] transition-all duration-300 ${
                    shareButtonStatus === true
                      ? 'visible opacity-100 top-full'
                      : 'opacity-0 invisible top-[130%]'
                  }`}
                  shareUrl={productUrl}
                />
              </div>
            </div> */}
            </div>
            {data?.tag && (
              <ul className="pt-5 xl:pt-6">
                <li className="relative inline-flex items-center justify-center text-sm md:text-15px text-brand-dark text-opacity-80 ltr:mr-2 rtl:ml-2 top-1">
                  <LabelIcon className="ltr:mr-2 rtl:ml-2" /> {t('text-tags')}:
                </li>
                {data?.tag?.map((item: any) => (
                  <li className="inline-block p-[3px]" key={`tag-${item.id}`}>
                    <TagLabel data={item} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
      <div className="mt-5  overflow-x-auto" id="you-might-also-like-section">
        <RelatedProductFeed
          carouselBreakpoint={breakpoints}
          className="items_might_like mb-0.5 md:mb-2 lg:mb-3.5 xl:mb-4 2xl:mb-6"
          hits={relatedItems}
          titleClassName=""
        />
      </div>
      <div className="bg-brand-graybg lg:-mt-4">
        {/*<div className="pb-10 sm:pb-20 border border-gray-300 pt-6 px-4 md:pl-6 lg:pl-8 2xl:pl-10">
           {data?.up_sell_products && (
            <FrequentlyBoughtTogetherFeed
              carouselBreakpoint={breakpoints}
              className="pt-5 pl-4"
              hits={relatedItems}
            />
          )} 
        </div>*/}
        {/* <ProductComparewithOthers /> */}
        <ProductReviewsAndQuestion
          sku={product.sku}
          product_id={product?.entity_id}
          reviews={reviews}
        />
      </div>

      {data?.is_in_stock && (
        <ProductStickyBar
          product={product}
          selectedVariation={selectedVariation}
          selectedOptions={selectedOptions}
          data={data}
          item={item}
          isSelected={isSelected}
          onQuantityChange={handleQuantityChange}
          selectedQuantity={selectedQuantity}
        />
      )}
    </div>
  )
}

export default ProductSingleDetails
